/**
 * Tutor row for the management {@link DataTable}.
 * Name, status, situation, contact, completion, and milestone cells.
 */
import * as React from 'react';
import { User, CalendarDays } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  TUTOR_REMOTE_MILESTONES as TUTOR_MILESTONES,
  type TutorRemoteTabView,
} from '@/components/tutors/tutor-configs';
import type { TutorRecord } from '@/types/remote/tutor.types';
import {
  canShowArchiveRow,
  canShowReactivateRow,
  deriveTutorStatusConfigs,
  deriveTutorContact,
  formatDate,
  getTutorRemoteMilestoneStatus as getTutorMilestoneStatus,
  type TutorRowPageActions,
} from './tutor-table-utils';

/** Props for {@link TutorRow}. */
interface TutorRowProps {
  record: TutorRecord;
  onView?: (id: string) => void;
  onEdit?: (id: string, record: TutorRecord) => void;
  onViewSessions?: (record: TutorRecord) => void;
  onViewReports?: (record: TutorRecord) => void;
  pageActions?: TutorRowPageActions;
  activeTab: TutorRemoteTabView;
  onArchive?: (userId: string) => void;
  onReactivate?: (userId: string) => void;
}

/** Tutor row for the management {@link DataTable}. */
export const TutorRow: React.FC<TutorRowProps> = ({
  record,
  onView,
  onEdit,
  onViewSessions,
  onViewReports,
  pageActions,
  activeTab,
  onArchive,
  onReactivate,
}) => {
  const { statusConfig, StatusIcon, situationConfig, SituationIcon } =
    deriveTutorStatusConfigs(record);

  const primaryContact = deriveTutorContact(record);

  const completionPct = record.completion.completedSectionsPercentage;
  const userStatus = record.user?.status;
  const showArchive = canShowArchiveRow(userStatus, pageActions?.canArchiveRow);
  const showReactivate = canShowReactivateRow(userStatus, activeTab, pageActions?.canReactivateRow);

  function handleArchive(): void {
    onArchive?.(record.userId);
  }

  function handleReactivate(): void {
    onReactivate?.(record.userId);
  }

  return (
    <TableRow>
      <td className="!px-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground leading-tight">
              {record.user?.firstName} {record.user?.lastName}
            </p>
            {record.registrationManager && (
              <p className="text-xs text-muted-foreground">
                Resp. : {record.registrationManager.firstName} {record.registrationManager.lastName}
              </p>
            )}
          </div>
        </div>
      </td>

      <td className="!px-3">
        <div className="flex flex-col gap-0.5">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 min-w-0">
                  <primaryContact.icon
                    className="w-4 h-4 shrink-0"
                    style={{ color: 'var(--color-1-nuance-1)' }}
                  />
                  <span className="text-sm text-foreground truncate max-w-[160px] cursor-default">
                    {primaryContact.value}
                  </span>
                </div>
              </TooltipTrigger>
              {primaryContact.value !== 'N/R' && (
                <TooltipContent>
                  <p>{primaryContact.value}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          {primaryContact.secondaryValue && (
            <span className="text-xs text-muted-foreground">{primaryContact.secondaryValue}</span>
          )}
        </div>
      </td>

      <td className="!px-3">
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--color-2-nuance-1)] rounded-full transition-all"
              style={{ width: `${completionPct}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground tabular-nums">{completionPct}%</span>
        </div>
      </td>

      <td className="!px-3">
        <div className="flex items-center gap-1.5">
          {TUTOR_MILESTONES.map((m) => {
            const mStatus = getTutorMilestoneStatus(record.sections, m.key);
            const Icon = m.icon;
            return (
              <div key={m.key} className="flex flex-col items-center gap-0.5" title={m.label}>
                <Icon
                  className="w-4 h-4"
                  style={{ color: mStatus === 'validated' ? m.validatedColor : m.pendingColor }}
                />
                <span
                  className="text-[10px]"
                  style={{ color: mStatus === 'validated' ? m.validatedColor : m.pendingColor }}
                >
                  {m.label}
                </span>
              </div>
            );
          })}
        </div>
      </td>

      <td className="!px-3">
        <div className="flex items-center gap-1">
          <CalendarDays className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm">{formatDate(record.createdAt)}</span>
        </div>
      </td>

      <td className="!px-3">
        <div className="flex flex-col items-start gap-1">
          <LabelBadge
            label={statusConfig?.label ?? record.onboardingStatus}
            icon={StatusIcon}
            variant={statusConfig?.badgeVariant ?? 'outlined'}
            color={statusConfig?.badgeColor}
            size="md"
          />
          <LabelBadge
            label={situationConfig.label}
            icon={SituationIcon}
            variant={situationConfig.badgeVariant}
            color={situationConfig.badgeColor}
            size="sm"
          />
        </div>
      </td>

      <td className="text-center !px-3">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView?.(record.id)} />
          {pageActions?.canEditRow && (
            <Button preset="row-edit" onClick={() => onEdit?.(record.userId, record)} />
          )}
          {onViewSessions && pageActions?.canViewSessions !== false && (
            <Button preset="row-view-sessions" onClick={() => onViewSessions(record)} />
          )}
          {onViewReports && pageActions?.canViewReports !== false && (
            <Button preset="row-view-reports" onClick={() => onViewReports(record)} />
          )}
          {showArchive ? <Button preset="row-archive" onClick={handleArchive} /> : null}
          {showReactivate ? <Button preset="row-reactivate" onClick={handleReactivate} /> : null}
        </div>
      </td>
    </TableRow>
  );
};
